import { useEffect, useState } from 'react'
import {
  ShippingStateValue,
  ThermalStorageUnitsIndexResponseComponent,
} from '../../types/typescript-axios'

import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { VixellTable } from '../../components/part/vixell-table'
import { TableHeaderContractAndManagementNo } from '../../components/part/table-headers/table-header-contract-and-management-no'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SearchInput } from '../../components/molecules/search-input'
import { Select } from '../../components/molecules/select'
import { Lang } from '../../translations/lang'
import { toFormattedDatetime } from '../../utils/to-formatted-datetime'
import { downloadCsv } from '../../utils/download-csv'
import { webRequest } from '../../types/handle-api'
import { usePaging } from '../../contexts/use-paging'
import { Cell } from '../../components/atom/Cell'
import { TableElementDatetime2Line } from '../../components/part/table-elements/table-element-datetime-2-line'
import { ShippingStateSelects } from './common/shipping-state-selects'

export const AdminThermalStorageUnits = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [thermalStorageUnits, setThermalStorageUnits] = useState<
    ThermalStorageUnitsIndexResponseComponent[]
  >([])

  const [searchWord, setSearchWord] = useState<string>('')
  const [searchShippingState, setSearchShippingState] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [till, setTill] = useState<string>('')

  const [loadNextPageAt, setLoadNextPageAt] = useState<Date>(new Date())
  const { currentPage, setPaging, stopPageLoad } = usePaging()
  const [searchParamsString, setSearchParamsString] = useState<string>()

  useEffect(() => {
    fetchDataAndSetStates()
  }, [searchParams])

  async function fetchDataAndSetStates(loadNextPage = false) {
    const response = await fetchData({ loadNextPage })
    if (response.status === 200) {
      if (searchParams.toString() === searchParamsString) {
        setThermalStorageUnits((units) => [
          ...units,
          ...response.data.thermalStorageUnits,
        ])
      } else {
        setThermalStorageUnits(response.data.thermalStorageUnits)
      }
      setPaging(response.data)
      setSearchParamsString(searchParams.toString())
    }
  }

  async function fetchData({ all = false, loadNextPage = false }) {
    const query = searchParams.get('search')
    const shippingState = searchParams.get('shippingState')
    const latestShippingStateDateTimeFrom = searchParams.get('from')
    const latestShippingStateDateTimeTill = searchParams.get('till')

    setSearchWord((w) => w || query || '')
    setSearchShippingState(shippingState || '')
    setFrom(latestShippingStateDateTimeFrom || '')
    setTill(latestShippingStateDateTimeTill || '')

    return await webRequest('ThermalStorageUnitsIndex', {
      ...(shippingState && {
        shippingState: shippingState as ShippingStateValue,
      }),
      ...(query && { query }),
      ...(latestShippingStateDateTimeFrom && {
        latestShippingStateDateTimeFrom,
      }),
      ...(latestShippingStateDateTimeTill && {
        latestShippingStateDateTimeTill,
      }),
      ...(all && { all: true }),
      ...(!all && loadNextPage && { page: currentPage + 1 }),
    })
  }

  async function csvDownload() {
    const response = await fetchData({ all: true })
    if (response.status === 200) {
      const csvData = [
        [
          Lang.thermalStorageUnitSerialNo,
          Lang.model,
          Lang.siteName,
          Lang.contractNo,
          Lang.managementNo,
          Lang.shippingState,
          Lang.shippingStateUpdatedDate,
        ],
        ...response.data.thermalStorageUnits.map(
          ({
            serialNo,
            model,
            siteName,
            fullContractNo,
            managementNo,
            shippingState,
            shippingStateUpdatedDateTime,
          }) => [
            serialNo,
            model,
            siteName,
            fullContractNo,
            managementNo,
            shippingState && Lang.shippingStateName[shippingState],
            toFormattedDatetime(shippingStateUpdatedDateTime),
          ]
        ),
      ]
      downloadCsv(
        csvData,
        `thermalStorageUnits-${new Date().toISOString()}.csv`
      )
    }
  }

  function infiniteLoad() {
    if (stopPageLoad) return
    if (moment(loadNextPageAt).isAfter(moment().subtract(3, 'seconds'))) return

    setLoadNextPageAt(new Date())
    fetchDataAndSetStates(true)
  }

  function selectShippingState(value: string) {
    if (value) searchParams.set('shippingState', value)
    if (!value) searchParams.delete('shippingState')
    navigate(`/thermal-storage-units?${searchParams.toString()}`)
  }

  function changeFrom(value: string) {
    if (value) searchParams.set('from', value)
    if (!value) searchParams.delete('from')
    navigate(`/thermal-storage-units?${searchParams.toString()}`)
  }

  function changeTill(value: string) {
    if (value) searchParams.set('till', value)
    if (!value) searchParams.delete('till')
    navigate(`/thermal-storage-units?${searchParams.toString()}`)
  }

  function search() {
    if (searchWord) searchParams.set('search', searchWord)
    if (!searchWord) searchParams.delete('search')
    navigate(`/thermal-storage-units?${searchParams.toString()}`)
  }

  const titleRightElement = (
    <div className="flex flex-col items-end">
      <div>
        <SearchInput
          value={searchWord}
          onChange={setSearchWord}
          onClick={search}
        />
      </div>
      <div className="flex mt-16 gap-[48px]">
        <div className="flex flex-col">
          <label>{Lang.shippingState}</label>
          <Select
            className="mt-8"
            value={searchShippingState}
            options={ShippingStateSelects}
            onChange={selectShippingState}
          />
        </div>
        <div>
          <label>{Lang.shippingStateUpdatedDate}</label>
          <div className="flex items-center gap-[12px]">
            <input
              type="date"
              className="w-[160px]"
              value={from}
              onChange={(e) => changeFrom(e.target.value)}
            />
            <span className="text-3xl text-gray-300">〜</span>
            <input
              type="date"
              className="w-[160px]"
              value={till}
              onChange={(e) => changeTill(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <DefaultWithTitleLayout
      title={Lang.admin.thermalStorageUnits.title}
      description={Lang.admin.thermalStorageUnits.description}
      titleRightElement={titleRightElement}
      infiniteLoad={infiniteLoad}
      csvDownload={csvDownload}
    >
      {thermalStorageUnits && (
        <VixellTable
          rootClassName="my-64"
          classNames={['w-200', 'w-200', 'w-200', 'w-200', 'w-200']}
          headers={[
            Lang.thermalStorageUnitSerialNo,
            Lang.model,
            Lang.siteName,
            <TableHeaderContractAndManagementNo key={1} />,
            Lang.shippingState,
            Lang.shippingState + Lang.updatedDate,
          ]}
          elements={thermalStorageUnits.map((unit) => [
            unit.serialNo,
            unit.model,
            unit.siteName,
            <Cell className="flex-col" layout="center_center" key={unit.id}>
              <div>{unit.fullContractNo}</div>
              <div>{unit.managementNo}</div>
            </Cell>,
            unit.shippingState && Lang.shippingStateName[unit.shippingState],
            unit.shippingStateUpdatedDateTime && (
              <TableElementDatetime2Line
                dateTime={unit.shippingStateUpdatedDateTime}
              />
            ),
          ])}
          onLineClick={(index) =>
            navigate(`/thermal-storage-units/${thermalStorageUnits[index].id}`)
          }
        />
      )}
    </DefaultWithTitleLayout>
  )
}
